import { SliderValueChangeDetails, UseSliderContext } from '@ark-ui/react';
import { Slider, SliderContext, SliderProps } from '@ui/lib/park/ui/slider';
import { objectFromEntries } from '@ui/utils/objectFromEntries';
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

export type LeverageSliderProps = Omit<
  SliderProps,
  'max' | 'marks' | 'defaultValue'
> & {
  children?: ReactNode;
  preventValue?: number; // value to prevent
  marks: {
    /** value use to determine position of the mark */
    sliderValue: number;
    /** actual value to return on value change */
    value: number;
    label?: ReactNode;
  }[];
};

export const LeverageSlider = forwardRef<HTMLDivElement, LeverageSliderProps>(
  (props, ref) => {
    const {
      children,
      marks,
      onValueChange,
      value,
      preventValue,
      ...rootProps
    } = props;
    const apiRef = useRef<{ api: UseSliderContext }>(null);
    const _value = value?.[0];
    const {
      _marks,
      sliderValueToValueMap,
      markSliderValues,
      valueToSliderValueMap,
    } = useMemo(() => {
      if (marks) {
        return {
          markSliderValues: marks.map((mark) => mark.sliderValue),
          sliderValueToValueMap: objectFromEntries(
            marks.map((mark) => [mark.sliderValue, mark.value]),
          ),
          valueToSliderValueMap: objectFromEntries(
            marks.map((mark) => [mark.value, mark.sliderValue]),
          ),
          _marks: marks.map((mark) => ({
            value: mark.sliderValue,
            label: mark.label,
            id: mark.value,
          })),
        };
      }

      return {};
    }, [marks]);
    const sliderValue = useMemo(() => {
      const sliderValue =
        _value === undefined
          ? undefined
          : valueToSliderValueMap?.[_value] ?? _value;
      const nextSliderValue = snapToSliderValue(
        sliderValue || 0,
        markSliderValues || [],
      );

      return [nextSliderValue];
    }, [_value, markSliderValues, valueToSliderValueMap]);

    const handleValueChange = useCallback(
      (v: SliderValueChangeDetails) => {
        const nextSliderValue = snapToSliderValue(
          v.value[0],
          markSliderValues || [],
        );
        const nextValue =
          sliderValueToValueMap?.[nextSliderValue] ?? nextSliderValue;
        // update slider position
        if (preventValue && v.value[0] > preventValue) {
          apiRef.current?.api.setValue([preventValue]); //slider value
          onValueChange?.({
            value: [preventValue], // actual value
          });
        } else {
          apiRef.current?.api.setValue([nextSliderValue]);
          onValueChange?.({
            value: [nextValue],
          });
        }
      },
      [markSliderValues, sliderValueToValueMap, preventValue, onValueChange],
    );

    return (
      <Slider
        ref={ref}
        max={_marks?.at(-1)?.value}
        marks={_marks}
        value={sliderValue}
        onValueChange={handleValueChange}
        preventValue={preventValue}
        {...rootProps}
      >
        <SliderContext>
          {(api) => {
            // disable rule of hooks, this can be a function component
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useImperativeHandle(apiRef, () => {
              return { api };
            });

            return children;
          }}
        </SliderContext>
      </Slider>
    );
  },
);

LeverageSlider.displayName = 'LeverageSlider';

function snapToSliderValue(value: number, markers: number[]): number {
  let nextValue = value;
  markers.sort((a, b) => b - a); // Sort markers in descending order
  if (nextValue < (markers.at(-1) ?? 0)) {
    // below lowest value, do not override
    return nextValue;
  }
  for (let i = 0; i < markers.length; i++) {
    if (markers[i + 1] === undefined) {
      // snap to lowest value
      nextValue = markers[i];
      break;
    }
    const middlePoint = (markers[i] + markers[i + 1]) / 2;
    if (value > middlePoint) {
      // snap to higher value
      nextValue = markers[i];
      break;
    }
  }

  return nextValue;
}
